import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { searchApi } from '../../api/search';
import { albumApi } from '../../api/album';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import ProgressBar from '../../components/common/ProgressBar';
import NoData from '../../components/common/NoData';
import PageLayout from '../../components/layout/PageLayout';
import Pagination from '../../components/common/Pagination';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useScrollToTopOnPageChange } from '../../hooks/useScrollToTopOnPageChange';
import SearchBar from '../../components/common/SearchBar';
import PageTitle from '../../components/common/PageTitle';
import theme from '../../theme';

// 10줄마다 페이지네이션 (xs: 2열, md: 4열, lg: 5열)
const ITEMS_PER_PAGE = { xs: 20, md: 40, lg: 50 };

const ReviewUpload = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const itemsPerPage = isXs ? ITEMS_PER_PAGE.xs : isMd ? ITEMS_PER_PAGE.md : ITEMS_PER_PAGE.lg;
  const category = searchParams.get('category') || 'releases'; // releases | recommended
  const type = searchParams.get('type') || 'albums'; // albums | singles
  const region = searchParams.get('region') || 'all'; // all | korean | global
  const sort = searchParams.get('sort') || 'popular';
  const period = searchParams.get('period') || 'week';
  const urlSearchQuery = searchParams.get('q') || '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const isAlbumUpload = location.pathname.startsWith('/albums');
  const pageTitle = isAlbumUpload ? '앨범' : 'Reviews';
  const forcedDisplayRegion =
    category === 'releases' ? { korean: 'KR', global: 'GLOBAL' }[region] : null;

  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [loading, setLoading] = useState(false);
  const [addingAlbums, setAddingAlbums] = useState(new Set()); // 로딩 중인 앨범들
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 초기 로딩 플래그

  // URL의 페이지 파라미터가 변경되면 상태 업데이트
  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  // 처음 로딩 시 page=1 파라미터가 없으면 추가 (초기 마운트 시에만 실행)
  useEffect(() => {
    if (!searchParams.get('page')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 초기 마운트 시에만 실행

  // 페이지 변경 시 스크롤을 상단으로 이동
  useScrollToTopOnPageChange();

  // 현재 페이지의 검색 결과 계산
  const currentPageResults = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allSearchResults.slice(startIndex, endIndex);
  }, [currentPage, allSearchResults, itemsPerPage]);

  // 총 페이지 수 계산
  const totalPages = Math.ceil(allSearchResults.length / itemsPerPage);

  const fetchAlbumsByQuery = useCallback(
    async (query, shouldResetPage = true) => {
      if (!query.trim()) {
        setAllSearchResults([]);
        shouldResetPage && setCurrentPage(1);
        return;
      }

      try {
        setLoading(true);
        const response = await searchApi.searchAllAlbums(query, category, type);
        const allResults = response.data || [];
        setAllSearchResults(allResults);
        shouldResetPage && setCurrentPage(1); // 새로운 검색 시에만 첫 페이지로 리셋
      } catch (error) {
        console.error('검색 중 오류 발생:', error);
        setAllSearchResults([]);
        shouldResetPage && setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    },
    [category, type],
  );

  // 초기 로딩 시 URL에 검색어가 있으면 데이터 로드
  useEffect(() => {
    if (isInitialLoad) {
      urlSearchQuery.trim() && fetchAlbumsByQuery(urlSearchQuery, false); // 페이지 리셋하지 않음
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, urlSearchQuery, fetchAlbumsByQuery]);

  // 검색어 입력 시 디바운스 처리 및 URL 업데이트
  useEffect(() => {
    // 초기 로딩 중이거나 URL 검색어와 같으면 스킵
    if (isInitialLoad || searchQuery === urlSearchQuery) {
      return;
    }

    const timer = setTimeout(() => {
      // 검색 실행
      if (searchQuery.trim()) {
        // URL 업데이트 - page 파라미터 보존
        setSearchParams(
          (prevParams) => {
            const newSearchParams = new URLSearchParams(prevParams);
            newSearchParams.set('q', searchQuery);
            newSearchParams.set('page', '1'); // 새로운 검색 시 첫 페이지로
            return newSearchParams;
          },
          { replace: true },
        );

        fetchAlbumsByQuery(searchQuery, true); // 새로운 검색이므로 페이지 리셋
      } else {
        // 검색어가 비어있으면 q 파라미터 제거
        setSearchParams(
          (prevParams) => {
            const newSearchParams = new URLSearchParams(prevParams);
            newSearchParams.delete('q');
            newSearchParams.set('page', '1');
            return newSearchParams;
          },
          { replace: true },
        );

        setAllSearchResults([]);
        setCurrentPage(1);
      }
    }, 1000); // 1초 디바운스

    return () => clearTimeout(timer);
  }, [searchQuery, urlSearchQuery, isInitialLoad, fetchAlbumsByQuery, setSearchParams]);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback(
    (event, newPage) => {
      // URL 업데이트 - replace: false로 설정하여 브라우저 히스토리에 추가
      setSearchParams(
        (prevParams) => {
          const newSearchParams = new URLSearchParams(prevParams);
          newSearchParams.set('page', newPage.toString());
          return newSearchParams;
        },
        { replace: false },
      );

      // currentPage는 urlPage에 의해 자동으로 업데이트됨 (34-36번 줄 useEffect)
    },
    [setSearchParams],
  );

  // 검색 실행 함수
  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        // URL 업데이트 - page 파라미터를 1로 리셋
        setSearchParams(
          (prevParams) => {
            const newSearchParams = new URLSearchParams(prevParams);
            newSearchParams.set('q', searchQuery);
            newSearchParams.set('page', '1');
            return newSearchParams;
          },
          { replace: true },
        );
        fetchAlbumsByQuery(searchQuery, true);
      }
    },
    [searchQuery, setSearchParams, fetchAlbumsByQuery],
  );

  // 앨범 추가 버튼 클릭
  const handleAddAlbum = async (album) => {
    const albumKey = `${album.albumSource}-${album.sourceId}`;
    if (addingAlbums.has(albumKey)) {
      return;
    }

    try {
      setAddingAlbums((prev) => new Set(prev).add(albumKey));
      if (category === 'recommended') {
        await albumApi.createRecommendAlbum(album.sourceId, album.albumSource);
      } else {
        await albumApi.createAlbum(album.sourceId, album.albumSource, forcedDisplayRegion);
      }
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });

      const destinationPath = isAlbumUpload ? '/albums' : '/reviews';
      const destinationParams = new URLSearchParams({
        category,
        type,
        page: '1',
      });

      if (isAlbumUpload) {
        destinationParams.set('region', region);
        destinationParams.set('sort', sort);
        if (category === 'releases') {
          destinationParams.set('period', period);
        }
      }

      navigate(`${destinationPath}?${destinationParams.toString()}`);
    } catch (error) {
      console.error('앨범 추가 중 오류 발생:', error);
      // 에러 발생 시 로딩 상태 해제
      setAddingAlbums((prev) => {
        const newSet = new Set(prev);
        newSet.delete(albumKey);
        return newSet;
      });
    }
  };

  return (
    <PageLayout>
      <Box
        mx={{ xs: -2.5, md: -3 }}
        px={{ xs: 2.5, md: 3 }}
        borderBottom={{ xs: `1px solid ${theme.palette.grey.line}`, md: 'none' }}
      >
        <PageTitle textAlign={{ xs: 'left', md: 'center' }} mb={{ xs: 2, md: 3.75, lg: 0 }}>
          {pageTitle}
        </PageTitle>
        <Box display="flex" justifyContent="center" alignItems="center" py={{ xs: 1, md: 2 }}>
          <SearchBar
            searchValue={searchQuery}
            setSearchValue={setSearchQuery}
            handleSearch={handleSearch}
          />
        </Box>
      </Box>

      {/* 검색 결과 */}
      <ProgressBar isLoading={loading}>
        {searchQuery.trim() && (
          <Box mt={{ xs: '30px', md: '30px', lg: '40px' }}>
            {allSearchResults.length > 0 ? (
              <Box>
                {/* 앨범 그리드 - 5개씩 10줄 */}
                <Grid container columnSpacing={{ xs: 2, lg: 2.5 }} rowSpacing={{ xs: 3, md: 5 }}>
                  {currentPageResults.map((album, index) => (
                    <Grid
                      item
                      xs={6}
                      md={3}
                      lg={2.4}
                      key={`${album.albumSource}-${album.sourceId}-${index}`}
                    >
                      <AlbumVerticalCard
                        sourceId={album.sourceId}
                        albumSource={album.albumSource}
                        slug={album.slug}
                        imageUrl={album.albumImageUrl}
                        albumName={album.title}
                        artistName={album.artist?.name}
                        artistId={album.artist?.sourceId}
                        artistSource={album.artist?.artistSource}
                        artistSlug={album.artist?.slug}
                        albumAvgRating={album.averageRating}
                        commentCount={album.commentCount}
                        bookmarkCount={album.bookmarkCount}
                        releaseDate={album.releaseDate}
                        isStarred={album.isStarred}
                        contentRating={album.contentRating}
                        isAddMode={true}
                        onAdd={() => handleAddAlbum(album)}
                        ratingSolo={!isLg}
                        dateSolo={isLg}
                      />
                    </Grid>
                  ))}
                </Grid>
                {/* 페이지네이션 */}
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
              </Box>
            ) : (
              <NoData message="결과 없음" />
            )}
          </Box>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default ReviewUpload;
